import React, { memo, useCallback, useMemo, useState } from "react";
import t from "../../../../../../../../i18n";
import Checkbox from "@ui/Checkbox";
import { FragmentSlot, OptionV2 } from "@ui/Option/v2";
import Text from "@ui/Text";
import { colors, spacings } from "@ui/Theme";
import { css } from "@linaria/core";
import ButtonIconV2, { ButtonIconMarker } from "@ui/ButtonIconV2";
import { EqualizerLine } from "@ui/IconV2/react";
import Menu from "@ui/Menu";
import TooltipV2 from "@ui/TooltipV2";

export const ANNOTATION_FAULT_STATUSES: Record<string, string> = {
  TO_BE_ANALYSED: t("project.dashboard.fault.status.to_analyse"),
  SUSPECTED: t("project.dashboard.fault.status.suspected"),
  CONFIRMED: t("project.dashboard.fault.status.confirmed"),
  REJECTED: t("project.dashboard.fault.status.false_detection"),
  NON_MODELLED: t("project.dashboard.fault.non_modelled"),
  NOT_DETECTED: t("project.dashboard.fault.not_detected"),
};

export const DiagramMenu = memo(
  ({
    expandedAnnotations,
    expandAnnotations,
    annotationsDisplay,
    displayAnnotations,
    isRoleDisplayed,
    setIsRoleDisplayed,
    resourceKindsUsed,
  }: {
    expandedAnnotations: boolean[];
    expandAnnotations: (v: React.SetStateAction<boolean[]>) => void;
    annotationsDisplay: { [key: string]: string[] };
    displayAnnotations: (v: React.SetStateAction<{ [key: string]: string[] }>) => void;
    isRoleDisplayed: boolean | undefined;
    setIsRoleDisplayed: React.Dispatch<React.SetStateAction<boolean | undefined>>;
    resourceKindsUsed: string[];
  }) => {
    const [menuOpened, setMenuOpened] = useState(false);

    const toggleInvalidMeasureDisplay = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        displayAnnotations(s => {
          const validity = new Set(s["validity"] ?? []);
          if (e.target.checked && !validity.has("INVALID_AUTO")) validity.add("INVALID_AUTO");
          if (e.target.checked && !validity.has("INVALID_AUTO_CROSS_FILTER"))
            validity.add("INVALID_AUTO_CROSS_FILTER");
          if (e.target.checked && !validity.has("INVALID_INHERITED"))
            validity.add("INVALID_INHERITED");
          if (e.target.checked && !validity.has("INVALID_MANUAL")) validity.add("INVALID_MANUAL");
          if (!e.target.checked && validity.has("INVALID_AUTO")) validity.delete("INVALID_AUTO");
          if (!e.target.checked && validity.has("INVALID_AUTO_CROSS_FILTER"))
            validity.delete("INVALID_AUTO_CROSS_FILTER");
          if (!e.target.checked && validity.has("INVALID_INHERITED"))
            validity.delete("INVALID_INHERITED");
          if (!e.target.checked && validity.has("INVALID_MANUAL"))
            validity.delete("INVALID_MANUAL");
          return { ...s, validity: Array.from(validity) };
        });
      },
      [displayAnnotations],
    );

    const toggleFaultStatusDisplay = useCallback(
      (status: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        displayAnnotations(s => {
          const statuses = s["faultStatus"] ? new Set(s["faultStatus"]) : new Set([]);
          if (e.target.checked && !statuses.has(status)) {
            statuses.add(status);
            if (status === "BACK_TO_BE_ANALYSED") statuses.add("BACK_TO_BE_ANALYSED");
            if (status === "REJECTED") statuses.add("LEGACY_REJECTED");
          }
          if (!e.target.checked && statuses.has(status)) {
            statuses.delete(status);
            if (status === "BACK_TO_BE_ANALYSED") statuses.delete("BACK_TO_BE_ANALYSED");
            if (status === "REJECTED") statuses.delete("LEGACY_REJECTED");
          }
          return { ...s, faultStatus: Array.from(statuses) };
        });
      },
      [displayAnnotations],
    );

    const toggleRoleDisplay = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsRoleDisplayed(e.target.checked);
      },
      [setIsRoleDisplayed],
    );

    const toggleAnnotationExpansions = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        expandAnnotations(s => s.map(() => e.target.checked)),
      [expandAnnotations],
    );

    const areAnnotationExpaneded = useMemo(
      () => expandedAnnotations.every(e => e === true),
      [expandedAnnotations],
    );

    const areInvalidMeasureDisplayed = useMemo(
      () =>
        annotationsDisplay["validity"]?.some(
          v =>
            v === "INVALID_MANUAL" ||
            v === "INVALID_AUTO" ||
            v === "INVALID_AUTO_CROSS_FILTER" ||
            v === "INVALID_INHERITED",
        ),
      [annotationsDisplay],
    );

    const isFaultStatusDisplayed = useCallback(
      (status: string) => annotationsDisplay["faultStatus"]?.some(v => v === status),
      [annotationsDisplay],
    );

    const hasOptionChecked = useMemo(
      () =>
        areAnnotationExpaneded ||
        areInvalidMeasureDisplayed ||
        isRoleDisplayed ||
        Object.keys(ANNOTATION_FAULT_STATUSES).some(status => isFaultStatusDisplayed(status)),
      [areAnnotationExpaneded, areInvalidMeasureDisplayed, isRoleDisplayed, isFaultStatusDisplayed],
    );

    return (
      <Menu
        opened={menuOpened}
        setOpened={setMenuOpened}
        defaultOpened={false}
        overlay={
          <div>
            <div className={diagramMenuSection}>
              <OptionV2>
                <Checkbox
                  slot="checkbox"
                  smallIndicator
                  checked={areAnnotationExpaneded}
                  onChange={toggleAnnotationExpansions}
                />
                <FragmentSlot slot="text">
                  {t("project.dashboard.block.diagram.menu.details")}
                </FragmentSlot>
              </OptionV2>
            </div>
            {resourceKindsUsed.includes("QUANTITY") && (
              <div className={diagramMenuSection}>
                <Text type="body2SemiBold" className={diagramMenuSectionTitle}>
                  {t("project.dashboard.block.diagram.menu.quantities")}
                </Text>
                <OptionV2>
                  <Checkbox
                    slot="checkbox"
                    smallIndicator
                    checked={areInvalidMeasureDisplayed}
                    defaultChecked
                    onChange={toggleInvalidMeasureDisplay}
                  />
                  <FragmentSlot slot="text">
                    {t("project.dashboard.block.diagram.menu.display_invalid")}
                  </FragmentSlot>
                  <FragmentSlot slot="description">
                    {t("project.dashboard.block.diagram.menu.display_invalid.description")}
                  </FragmentSlot>
                </OptionV2>
                <OptionV2>
                  <Checkbox
                    slot="checkbox"
                    smallIndicator
                    checked={isRoleDisplayed}
                    onChange={toggleRoleDisplay}
                  />
                  <FragmentSlot slot="text">
                    {t("project.dashboard.block.diagram.menu.display_roles")}
                  </FragmentSlot>
                </OptionV2>
              </div>
            )}
            {resourceKindsUsed.includes("FAULT") && (
              <div className={diagramMenuSection}>
                <Text type="body2SemiBold" className={diagramMenuSectionTitle}>
                  {t("project.dashboard.block.diagram.menu.faults")}
                </Text>
                {Object.keys(ANNOTATION_FAULT_STATUSES).map(status =>
                  status ? (
                    <OptionV2 key={status}>
                      <Checkbox
                        slot="checkbox"
                        smallIndicator
                        checked={isFaultStatusDisplayed(status)}
                        defaultChecked
                        onChange={toggleFaultStatusDisplay(status)}
                      />
                      <FragmentSlot slot="text">{ANNOTATION_FAULT_STATUSES[status]}</FragmentSlot>
                    </OptionV2>
                  ) : null,
                )}
              </div>
            )}
          </div>
        }
        placement="right"
        gap={16}
      >
        <div onClick={() => setMenuOpened(s => !s)} className={diagramMenuControl}>
          <TooltipV2 overlay={t("project.dashboard.block.diagram.menu")}>
            <ButtonIconV2>
              {hasOptionChecked && <ButtonIconMarker />}
              <EqualizerLine />
            </ButtonIconV2>
          </TooltipV2>
        </div>
      </Menu>
    );
  },
);

const diagramMenuSection = css`
  :not(:first-child) {
    border-top: 1px solid ${colors.titanium100};
    margin: ${spacings.xs} 0;
  }

  :last-child {
    margin-bottom: 0;
  }
`;

const diagramMenuSectionTitle = css`
  margin: ${spacings.xs} 0;
  color: ${colors.carbon700};
`;

const diagramMenuControl = css`
  align-items: center;
  background-color: ${colors.hydrogen};
  border: 1px solid ${colors.titanium200};
  cursor: pointer;
  display: flex;
  height: 35px;
  justify-content: center;
  padding: 0;
  width: 35px;
  border-radius: ${spacings.xs};
`;
